import type { CityAddressType } from '@/state/types';

/**
 * Phase 7.2.1 — City-size scaling factor for mission spawn frequency.
 *
 * Larger cities → more incidents per minute, smaller villages → fewer.
 * Pulls from Nominatim's `addresstype` (village/town/city/suburb) plus the
 * optional `population` tag from OSM relation/place nodes.
 */

export const CITY_FACTOR_BOUNDS = { min: 0.5, max: 2.5 } as const;

export function cityFactorFromAddressType(t: CityAddressType | undefined): number {
  switch (t) {
    case 'village':
      return 0.5;
    case 'suburb':
      return 0.8;
    case 'town':
      return 1.0;
    case 'city':
      return 1.8;
    default:
      return 1.0;
  }
}

export function cityFactorFromPopulation(pop: number | undefined): number | null {
  if (pop === undefined || !Number.isFinite(pop) || pop <= 0) return null;
  // Smooth log-ish ramp: 1k → 0.5, 10k → 1.0, 100k → 1.7, 1M+ → 2.5
  if (pop < 1_000) return 0.5;
  if (pop < 5_000) return 0.7;
  if (pop < 20_000) return 1.0;
  if (pop < 100_000) return 1.5;
  if (pop < 500_000) return 1.9;
  if (pop < 1_500_000) return 2.2;
  return CITY_FACTOR_BOUNDS.max;
}

/**
 * Combined factor: prefers population (if present), falls back to addressType.
 * Always returns a value in CITY_FACTOR_BOUNDS.
 */
export function getCityFactor(
  population: number | undefined,
  addressType: CityAddressType | undefined,
): number {
  const fromPop = cityFactorFromPopulation(population);
  const fromType = cityFactorFromAddressType(addressType);
  const raw = fromPop ?? fromType;
  return Math.max(CITY_FACTOR_BOUNDS.min, Math.min(CITY_FACTOR_BOUNDS.max, raw));
}
